import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const tutorialJsonLd = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Article',
      '@id': 'https://itaobuyspread.top/tutorial-itaobuy-spreadsheet/#article',
      headline: 'Itaobuy Spreadsheet Tutorial: Strategie Avanzate per Acquirenti Esperti',
      description: 'Tutorial avanzato su Itaobuy Spreadsheet: filtri personalizzati, analisi trend di prezzo, automazione e strategie per esperti.',
      image: 'https://readdy.ai/api/search-image?query=Professional%20data%20analyst%20workspace%20with%20dual%20monitors%20showing%20spreadsheets%20and%20product%20analytics%20dashboards%2C%20warm%20ambient%20lighting%2C%20modern%20Italian%20office%20with%20cream%20walls%2C%20sophisticated%20setup%20with%20premium%20peripherals%2C%20focused%20productive%20atmosphere%2C%20warm%20terracotta%20and%20sand%20tones%2C%20editorial%20photography%2C%20expert%20level%20workspace%20concept&width=1200&height=600&seq=tutorial-hero&orientation=landscape',
      author: { '@type': 'Organization', name: 'Itaobuy Team', url: 'https://itaobuyspread.top' },
      publisher: { '@id': 'https://itaobuyspread.top/#organization' },
      mainEntityOfPage: { '@type': 'WebPage', '@id': 'https://itaobuyspread.top/tutorial-itaobuy-spreadsheet' },
      datePublished: '2026-07-14',
      dateModified: '2026-07-14',
      inLanguage: 'it',
      isPartOf: { '@id': 'https://itaobuyspread.top/#website' },
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://itaobuyspread.top' },
        { '@type': 'ListItem', position: 2, name: 'Tutorial Avanzato', item: 'https://itaobuyspread.top/tutorial-itaobuy-spreadsheet' },
      ],
    },
  ],
};

export default function TutorialPage() {
  return (
    <>
      <Navbar />
      <main>
        <article>
          <header className="pt-32 pb-16 md:pt-40 md:pb-20 px-6 md:px-10 bg-background-50">
            <div className="max-w-4xl mx-auto text-center">
              <span className="inline-block px-4 py-1.5 rounded-full bg-accent-100/80 text-accent-800 text-xs font-semibold tracking-wide uppercase mb-5">
                Tutorial Avanzato
              </span>
              <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-foreground-950 leading-tight mb-6">
                Itaobuy Spreadsheet Tutorial: Strategie Avanzate per Acquirenti Esperti
              </h1>
              <div className="flex items-center justify-center gap-4 text-sm text-foreground-500">
                <span>Da Itaobuy Team</span>
                <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                <span>Aggiornato: Luglio 2026</span>
                <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                <span>12 min di lettura</span>
              </div>
            </div>
          </header>

          <div className="max-w-4xl mx-auto px-6 md:px-10">
            <div className="mb-12 overflow-hidden rounded-[32px]">
              <img
                src="https://readdy.ai/api/search-image?query=Professional%20data%20analyst%20workspace%20with%20dual%20monitors%20showing%20spreadsheets%20and%20product%20analytics%20dashboards%2C%20warm%20ambient%20lighting%2C%20modern%20Italian%20office%20with%20cream%20walls%2C%20sophisticated%20setup%20with%20premium%20peripherals%2C%20focused%20productive%20atmosphere%2C%20warm%20terracotta%20and%20sand%20tones%2C%20editorial%20photography%2C%20expert%20level%20workspace%20concept&width=1200&height=600&seq=tutorial-hero&orientation=landscape"
                alt="Itaobuy Spreadsheet tutorial avanzato"
                title="Itaobuy Spreadsheet - Tutorial Avanzato"
                className="w-full aspect-[2/1] object-cover object-top"
              />
            </div>

            <div className="prose prose-lg max-w-none mb-16">
              <section className="mb-14">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-5">
                  Oltre le Basi: Strategie per Esperti
                </h2>
                <p className="text-foreground-700 leading-relaxed mb-4">
                  Se conosci gi&agrave; le basi di Itaobuy Spreadsheet, &egrave; il momento di portare il tuo shopping al <strong>livello successivo</strong>. In questo tutorial avanzato esploreremo tecniche e strategie che ti permetteranno di individuare le migliori offerte prima degli altri e massimizzare il valore di ogni acquisto.
                </p>
                <p className="text-foreground-700 leading-relaxed">
                  <Link to="/" className="text-primary-600 font-semibold hover:text-primary-700 underline underline-offset-4">
                    Torna alla homepage di Itaobuy Spreadsheet Italia
                  </Link> per le risorse base.
                </p>
              </section>

              <div className="space-y-6 mb-14">
                {[
                  { title: 'Filtri Personalizzati Multi-Livello', desc: 'Invece di usare i filtri di base, crea viste personalizzate che combinano pi&ugrave; criteri contemporaneamente. Ad esempio: prodotti con valutazione &gt;4.7, prezzo &lt;€50 e tempi di spedizione &lt;15 giorni. Questo ti permette di identificare istantaneamente le migliori opportunit&agrave; senza scorrere centinaia di righe.' },
                  { title: 'Analisi dei Trend di Prezzo', desc: 'Tieni traccia dei prezzi nel tempo creando una copia settimanale dello spreadsheet. Confrontando i dati storici, puoi identificare pattern stagionali e prevedere quando un prodotto raggiunger&agrave; il prezzo pi&ugrave; basso. I mesi di gennaio e luglio sono spesso i migliori per trovare sconti significativi.' },
                  { title: 'Valutazione Ponderata dei Venditori', desc: 'Non fermarti alla valutazione media. Analizza il rapporto tra numero di vendite e recensioni, la distribuzione delle valutazioni e la velocit&agrave; di risposta. Un venditore con 1000 vendite e 4.6 stelle pu&ograve; essere pi&ugrave; affidabile di uno con 50 vendite e 5.0 stelle.' },
                  { title: 'Cross-Referencing tra Categorie', desc: 'Alcuni prodotti appaiono in categorie diverse con prezzi diversi. Impara a fare cross-referencing per trovare lo stesso prodotto a un prezzo inferiore in una categoria meno popolare.' },
                  { title: 'Automazione con Google Sheets', desc: 'Importa i dati di Itaobuy Spreadsheet in Google Sheets usando la funzione IMPORTRANGE. Configura avvisi automatici che ti notificano quando un prodotto scende sotto una certa soglia di prezzo o quando appare un nuovo venditore con valutazione elevata.' },
                ].map((tip, i) => (
                  <div key={i} className="p-6 rounded-2xl bg-background-50 border border-background-200/60">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                        <span className="text-primary-600 font-bold text-sm">{i + 1}</span>
                      </div>
                      <h3 className="font-heading text-lg font-bold text-foreground-950">{tip.title}</h3>
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed ml-11">{tip.desc}</p>
                  </div>
                ))}
              </div>

              <section className="mb-14">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-5">
                  Conclusione
                </h2>
                <p className="text-foreground-700 leading-relaxed mb-4">
                  Applicando queste strategie avanzate, potrai trasformare Itaobuy Spreadsheet da un semplice strumento di consultazione a un vero e proprio <strong>sistema di intelligence per lo shopping</strong>.
                </p>
                <p className="text-foreground-700 leading-relaxed">
                  <Link to="/" className="text-primary-600 font-semibold hover:text-primary-700 underline underline-offset-4">
                    Esplora la homepage di Itaobuy Spreadsheet
                  </Link> per mettere in pratica queste strategie.
                </p>
              </section>
            </div>

            <div className="mb-20 p-8 rounded-[28px] bg-primary-500 text-center">
              <h3 className="font-heading text-2xl font-bold text-background-50 mb-3">
                Metti in Pratica le Strategie
              </h3>
              <p className="text-primary-100 mb-6 max-w-lg mx-auto">
                Accedi ora e applica queste tecniche avanzate per trovare le migliori offerte.
              </p>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-background-50 text-sm font-semibold text-primary-600 hover:bg-background-100 transition-all cursor-pointer whitespace-nowrap"
              >
                Itaobuy Sheet
                <i className="ri-arrow-right-up-line"></i>
              </a>
            </div>
          </div>

          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(tutorialJsonLd),
            }}
          />
        </article>
      </main>
      <Footer />
    </>
  );
}